class ProductSelector extends HTMLElement {
  constructor() {
    super();
    this.inputWrapper = '[data-input-wrapper]';
    this.variants = JSON.parse(
      this.querySelector('[type="application/json"]').textContent
    );
    this.form = document.querySelector(
      `#ProductForm-${this.dataset.sectionId}`
    );
    this.purchaseOptions = document.querySelector(
      `#Product-Purchase-Options-${this.dataset.sectionId}`
    );
    this.productBar = document.querySelector(
      `#ProductBar-${this.dataset.sectionId}`
    );
    this.installmentsForm = document.querySelector(
      `#Product-Installments-${this.dataset.installmentsFormId}`
    );

    this.addEventListener('change', this.onVariantChange.bind(this));

    this.updateOptions();
    if (this.updateVariantStatuses()) this.updateOptions();
    this.updateVariant();
    this.initUpdateMedia();
    this.normaliseInitialQuantity();
    // this.form.toggleAddButton(!this.currentVariant.available, '');
  }

  updateVariantStatuses() {
    const inputWrappers = [...this.querySelectorAll(this.inputWrapper)];
    let reselected = false;

    inputWrappers.forEach((wrapper, index) => {
      // The first option (top-level axis) is always shown in full.
      if (index === 0) return;

      // Values selected in all PRIOR option positions (option1..optionIndex).
      const priorSelected = [];
      for (let p = 0; p < index; p++) {
        const checked = inputWrappers[p].querySelector(':checked');
        priorSelected.push(checked ? checked.value : null);
      }

      // A value here is POSSIBLE if a variant EXISTS whose earlier options
      // match the prior selection (existence only — impossible combos are
      // hidden). A value is AVAILABLE if at least one such variant is in stock;
      // possible-but-not-available values are greyed as sold out but stay
      // visible so the customer can see the sold-out state.
      const matchingVariants = this.variants.filter((variant) =>
        priorSelected.every((val, i) => variant[`option${i + 1}`] === val)
      );
      const possibleValues = matchingVariants.map(
        (variant) => variant[`option${index + 1}`]
      );
      const availableValues = matchingVariants
        .filter((variant) => variant.available)
        .map((variant) => variant[`option${index + 1}`]);

      const optionInputs = [
        ...wrapper.querySelectorAll('input[type="radio"], option')
      ];

      let firstVisibleInput = null;
      let checkedIsHidden = false;

      optionInputs.forEach((input) => {
        const value = input.getAttribute('value');
        const isPossible = possibleValues.includes(value);
        const isSoldOut = isPossible && !availableValues.includes(value);

        this.setOptionHidden(input, !isPossible, isSoldOut);

        if (isPossible && !firstVisibleInput) firstVisibleInput = input;
        if (input.checked && !isPossible) checkedIsHidden = true;
      });

      // Integrity fix: if the currently-checked value just became an
      // impossible combo, move the selection to the first valid value so what
      // the customer sees always maps to a real variant (prevents the wrong
      // item being added to cart).
      if (checkedIsHidden && firstVisibleInput) {
        firstVisibleInput.checked = true;
        optionInputs.forEach((input) => {
          if (input.nextElementSibling) {
            input.nextElementSibling.classList.toggle(
              'is-active',
              input.checked
            );
          }
        });
        reselected = true;
      }
    });

    return reselected;
  }

  setOptionHidden(input, hidden, soldOut = false) {
    input.disabled = hidden;

    if (input.tagName.toLowerCase() === 'option') {
      input.hidden = hidden;
      const dropdown = input.closest('dropdown-input');
      if (dropdown) {
        const escaped = input.value.replace(/"/g, '\\"');
        const button = dropdown.querySelector(
          `button[data-value="${escaped}"]`
        );
        if (button) {
          button.disabled = hidden;
          button.classList.toggle('is-impossible-combo', hidden);
          button.classList.toggle('is-sold-out', soldOut);
        }
      }
    } else {
      const li = input.closest('li');
      const target = li || input.nextElementSibling;
      if (target) {
        target.classList.toggle('is-impossible-combo', hidden);
        target.classList.toggle('is-sold-out', soldOut);
      }
    }

    // Mirror visibility into the product bar (sticky) if present.
    if (this.productBar) {
      const inputId = input.dataset.id || input.id;
      const productBarInput = this.productBar.querySelector(
        `option[data-id="${inputId}"]`
      );
      if (productBarInput) {
        productBarInput.disabled = hidden;
        productBarInput.hidden = hidden;
      }
    }
  }

  onVariantChange(event) {
    if (event.target.type === 'number') return;
    // The hidden [name="id"] input lives INSIDE <product-selector>. When we
    // sync it via updateVariantInput() we dispatch a bubbling 'change' that
    // re-enters this handler. Ignore it to prevent an infinite re-entry loop.
    if (event.target.name === 'id') return;
    this.updateOptions();

    // Hide impossible combos and auto-correct any now-impossible selection
    // BEFORE resolving the variant, so the variant + cart id are always valid.
    const reselected = this.updateVariantStatuses();
    if (reselected) this.updateOptions();

    this.updateVariant();

    // Safety: never proceed (or write a cart id) without a resolved variant.
    if (!this.currentVariant) return;

    this.productBarUpdateOptions();
    this.updatePickupAvailability();
    this.form.removeAttribute('data-has-selling-plan');

    // this.form.toggleAddButton(false, '');
    this.form.handleErrorMessage();

    this.updateMedia();
    this.updateURL();
    this.updateVariantInput();
    // Swap the displayed price instantly from local variant data so it doesn't
    // wait on the Section Rendering fetch below (which can take a few seconds).
    this.updateDisplayedPriceFast();
    this.renderProductInfo();

    // Sync selected options into sticky dropdowns
    const checkedOptions = document.querySelectorAll(
      'product-selector input[type="radio"]:checked'
    );
    const selectedOptions = [];
    checkedOptions.forEach((element) => {
      selectedOptions.push({
        name: element.name,
        value: element.value
      });
    });
    selectedOptions.forEach((selectedOption) => {
      let str = selectedOption.name;
      let match = str.match(/\[(.*?)\]/);
      let selectedOptionName = match ? match[1] : null;
      const stickyOptionElement = document.querySelector(
        `.product-size-dropdown__select-new[data-option-name="${selectedOptionName}"]`
      );
      if (stickyOptionElement) {
        stickyOptionElement.value = selectedOption.value;
        stickyOptionElement.dispatchEvent(
          new Event('change', { bubbles: true })
        );
      }
    });
  }

  updateOptions() {
    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.nextElementSibling.classList.toggle(
        'is-active',
        input.checked
      );
    });
    this.options = Array.from(
      this.querySelectorAll('input[type="radio"]:checked, select'),
      (el) => ({ name: el.dataset.name || el.name, value: el.value })
    );

    // Resolve the current variant from the selected options (existence-based).
    this.currentVariant = this.getVariantData();

    if (this.currentVariant) {
      let currentlyViewing = document.querySelector('.product__selector');
      currentlyViewing.dataset.price = this.currentVariant.price;

      let optionValues = [
        this.currentVariant.option1,
        this.currentVariant.option2,
        this.currentVariant.option3
      ];
      let optionElements = currentlyViewing.querySelectorAll(
        '.product-selector__label .product-selector__label--value'
      );

      optionElements.forEach((el, index) => {
        if (optionValues[index]) {
          el.textContent = optionValues[index];
        }
      });

      // Price calculation
      let price = parseInt(this.currentVariant.price, 10);
      document.querySelector(
        '.product-item.currently-viewing'
      ).dataset.price = price; // Update the price in currently-viewing card

      document
        .querySelectorAll('input.related-product-checkbox:checked')
        .forEach((input) => {
          price += parseInt(input.dataset.price, 10);
        });

      const priceNode = document.querySelector('.cs_price');
      priceNode.dataset.price = price; // Also update the data-price in the bundle price
      priceNode.textContent = `$${(price / 100)
        .toFixed(2)
        .replace(/\.00$/, '')}`;
    }
  }

  productBarUpdateOptions() {
    if (this.productBar) {
      Array.from(this.productBar.querySelectorAll('select')).map(
        (selector) => {
          selector.value = this.options.find(
            (option) => option.name === selector.name
          ).value;
        }
      );
      this.productBar.querySelectorAll('dropdown-input').forEach(
        (dropdown) => {
          dropdown.update();
        }
      );
    }
  }

  updateInventoryNotice(html) {
    const destination = document.querySelector('[data-inventory-notice]');
    const source = html.querySelector('[data-inventory-notice]');

    if (source && destination) destination.innerHTML = source.innerHTML;

    // Pass the inventory quantity from source to the quantity input
    const inventoryQuantityMatch = source?.textContent.match(/\d+/);
    const inventoryQuantity = inventoryQuantityMatch
      ? parseInt(inventoryQuantityMatch[0], 10)
      : 0;
    this.updateQuantityInput(inventoryQuantity);
  }

  updateQuantityInput(inventoryQuantity) {
    const selectedVariantData = this.getVariantData();
    const quantityInput = document.querySelector(
      '.product-selector__quantity quantity-input input'
    );
    if (!quantityInput) return;

    let productUsesInventory = true;

    // check inventory is active
    if (
      selectedVariantData &&
      selectedVariantData.inventory_management === null
    ) {
      productUsesInventory = false;
    }

    // check sell on sold out is active
    if (
      selectedVariantData &&
      selectedVariantData.inventory_quantity !== null &&
      inventoryQuantity === 0 &&
      selectedVariantData.available === true
    ) {
      productUsesInventory = false;
    }

    if (productUsesInventory) {
      quantityInput.setAttribute('max', inventoryQuantity);
      if (quantityInput.value > inventoryQuantity) {
        quantityInput.value = inventoryQuantity;
      }
    } else {
      quantityInput.setAttribute('max', '');
    }

    // Normalise quantity for purchasable variants so it never persists at 0
    // across a sold-out -> in-stock cycle (a 0 value fails checkValidity and
    // silently bails the add-to-cart submit). Runs on every variant change,
    // after any clamp-down above.
    const isPurchasable = selectedVariantData && selectedVariantData.available === true;
    if (isPurchasable) {
      quantityInput.setAttribute('min', '1');
      if (!(parseInt(quantityInput.value, 10) >= 1)) {
        quantityInput.value = 1;
      }
    }
  }

  // Run the same quantity normalisation on initial page load that a variant
  // change runs. Without it, landing on a pre-order / sold-out-but-sellable
  // variant leaves quantity at 0 — which fails form.checkValidity() and makes
  // Add to Cart silently do nothing until the shopper toggles variants. (The
  // June-9 fix only covered the variant-change path, not first load.)
  // Deferred to DOMContentLoaded so the quantity input is guaranteed present.
  normaliseInitialQuantity() {
    const run = () => {
      const inventoryNotice = document.querySelector('[data-inventory-notice]');
      const match = inventoryNotice ? inventoryNotice.textContent.match(/\d+/) : null;
      const inventoryQuantity = match ? parseInt(match[0], 10) : 0;
      this.updateQuantityInput(inventoryQuantity);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
  }

  updatePickupAvailability() {
    const pickUpAvailabilityNode = document.querySelector(
      'pickup-availability'
    );

    if (!pickUpAvailabilityNode || !this.currentVariant) return;

    pickUpAvailabilityNode.dataset.variantId = this.currentVariant.id;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailabilityNode.setAttribute('available', '');
      pickUpAvailabilityNode.fetchAvailability(
        pickUpAvailabilityNode.dataset.variantId
      );

      return;
    }

    pickUpAvailabilityNode.removeAttribute('available');
    pickUpAvailabilityNode.innerHTML = '';
  }

  updateVariant() {
    this.currentVariant = this.getVariantData();
  }

  getVariantData() {
    if (this.options.length === 0) {
      return this.variants[0];
    }

    return this.variants.find((variant) => {
      // Check if the variant's options match the selected options
      return this.options.every((option) => {
        const optionIndex =
          this.options.findIndex((opt) => opt.name === option.name) + 1;
        return variant[`option${optionIndex}`] === option.value;
      });
    });
  }

  initUpdateMedia() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = parseInt(urlParams.get('variant'), 10);

    if (variantIdFromUrl) {
      this.currentVariant = this.variants.find(
        (variant) => variant.id === variantIdFromUrl
      );

      if (this.currentVariant) {
        this.updateMedia();
      }
    }
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;

    const productMedia = document.querySelector('product-media');
    productMedia.setActiveMedia(this.currentVariant.featured_media.id);
  }

  updateURL(sellingPlanId) {
    if (!this.currentVariant) return;
    const params = new URLSearchParams(window.location.search);
    params[params.has('variant') ? 'set' : 'append'](
      'variant',
      this.currentVariant.id
    );
    if (sellingPlanId) {
      params[params.has('selling_plan') ? 'set' : 'append'](
        'selling_plan',
        sellingPlanId
      );
    } else {
      params.delete('selling_plan');
    }
    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}?${params.toString()}`
    );
  }

  updateVariantInput() {
    const inputs = [
      this.querySelector('[name="id"]'),
      this.installmentsForm?.querySelector('[name="id"]')
    ];
    inputs.forEach((input) => {
      if (!input) return;
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  setUnavailable() {
    const submitButton = this.form.submitButton;
    const price = this.querySelector(
      `#Product-Price-${this.dataset.sectionId}`
    );

    if (!submitButton) return;
    submitButton.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
  }

  updateDisplayedPriceFast() {
    if (!this.currentVariant) return;
    const text = `$${(parseInt(this.currentVariant.price, 10) / 100)
      .toFixed(2)
      .replace(/\.00$/, '')}`;
    [
      `#Product-Price-${this.dataset.sectionId}`,
      `#Product-Price-Bar-${this.dataset.sectionId}`
    ].forEach((selector) => {
      const node = document.querySelector(selector);
      if (!node) return;
      // Sale layout puts the live amount in <ins>; regular layout in the
      // visible (non-hidden, non-discount) span. Update whichever is present.
      const saleNode = node.querySelector('.price__sale ins');
      if (saleNode) {
        saleNode.textContent = text;
      } else {
        const regularNode = node.querySelector(
          '.price__regular span:not(.visually-hidden):not(.price-discount)'
        );
        if (regularNode) regularNode.textContent = text;
      }
    });

    // Patch the price inside the Add to Cart button instantly. The button's
    // full text (Add to Cart / Pre-Order / Sold Out) is still resolved by the
    // /variants/{id}.js fetch in product-form.js a moment later — we only swap
    // the price token, and only when the button currently shows one.
    const submitButton = this.form && this.form.submitButton;
    if (submitButton && /\$\d/.test(submitButton.textContent)) {
      submitButton.textContent = submitButton.textContent.replace(
        /\$[\d.,]+/,
        text
      );
    }
  }

  renderProductInfo() {
    const params = new URLSearchParams(window.location.search);
    params[params.has('variant') ? 'set' : 'append'](
      'variant',
      this.currentVariant.id
    );
    params.append('section_id', this.dataset.sectionId);

    fetch(`${this.dataset.url}?${params.toString()}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );
        [
          `#Product-Price-${this.dataset.sectionId}`,
          `#Product-Price-Bar-${this.dataset.sectionId}`,
          `#Product-Purchase-Options-${this.dataset.sectionId}`,
          `#Product-Badge-${this.dataset.sectionId}`,
          `#Product-Badge-Mobile-${this.dataset.sectionId}`,
          `#Product-Color-Value-${this.dataset.sectionId}`,
          `#Product-Purchase-Options-${this.dataset.sectionId}`
        ].map((liveRegionSelector) => {
          const destination = document.querySelector(liveRegionSelector);
          const source = html.querySelector(liveRegionSelector);

          this.updateInventoryNotice(html);

          if (destination && source) {
            destination.classList.remove('visibility-hidden');
            destination.innerHTML = source.innerHTML;
          }
        });
      });
  }
}

customElements.define('product-selector', ProductSelector);